<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, A11y, Thumbs } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import type { PaginationOptions } from 'swiper/types'
import { useUtilDetectAllStore } from '~/store/common/index'
import { useVideos } from '~/pages/goods/composables/ui'
import Videos from './videos.vue'
import 'swiper/css/thumbs'
import GoodsImageDetail from '~/components/common/modules/units/goods-image-detail.vue'
import { MOBILE_IMAGE_SIZE } from '~/constants/common/index'
import { ItemInfo } from '~/api/types/goods'
import ModalDetailView from '~/pages/goods/components/layer/modal-detail-view.vue'
import { LIVE_BROADCAST } from '~/pages/goods/constants'

interface Reactive {
  videoSrc: string
  isShowLayer: boolean
  isGroupItem?: boolean
}

export interface Props {
  stopWatch?: string
  items: ItemInfo
  isGroupItem?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  stopWatch: '', // stopWatch 기본값을 빈 문자열로 설정
  isGroupItem: false
})

const emit = defineEmits<(event: 'onLoad', result: boolean) => void>()

// variables
const { isShowVideo, showVideo, hideVideo, checkWifi, isAutoVideo } = useVideos()
const detectAllStore = useUtilDetectAllStore()
const { isDesktop } = storeToRefs(detectAllStore)

const [thumbsSwiper] = [ref<SwiperType | null>(null)]

const state = reactive<Reactive>({
  videoSrc: '',
  isShowLayer: false
})

const isImgLoaded = ref<boolean>(false)

const isTimeOut = computed(() => !props?.stopWatch || props?.stopWatch === '00:00:00')
const goodsInfo = toRef(props, 'items')

const liveVodFilePath = ref(LIVE_BROADCAST.LIVE.URL.ETC)
const shopPlusVodFilePath = ref(LIVE_BROADCAST.LIVE.URL.SHOPPLUS)
const pastVodFilePath = computed(() => goodsInfo.value?.brdctInfo?.pastVod)

const isPagination: PaginationOptions | boolean =
  goodsInfo.value?.detailInfo?.photoList && goodsInfo.value?.detailInfo?.photoList.length > 1
    ? { el: '.img-swiper .pagination', type: 'fraction' }
    : false

// functions
const setThumbsSwiper = (swiper: SwiperType) => {
  thumbsSwiper.value = swiper
}
const resetThumbsSwiper = () => {
  thumbsSwiper.value?.destroy()
  thumbsSwiper.value = null
}

const isLiveGoods = computed(() => {
  if (props.isGroupItem) return false

  const detailInfo = goodsInfo.value?.detailInfo
  const brdctInfo = goodsInfo.value?.brdctInfo
  if (detailInfo?.busChnId === 'TV' || detailInfo?.busChnId === 'CTCOM') {
    if (brdctInfo?.liveCd === 'A') {
      return true
    }
  }
})

// const isPaginationLength = computed(() => {
//   const photoList = goodsInfo.value?.detailInfo?.photoList
//   if (photoList && photoList.length > 1) {
//     return true
//   }
// })

const goodsPhotoList = computed(() => {
  let photoList = goodsInfo.value?.detailInfo?.photoList
  if (photoList && photoList.length > 0) {
    if (props.isGroupItem) {
      photoList = photoList.slice(0, 1)
    }
    return photoList
  } else {
    return ['noImg']
  }
})

watch(
  () => props.stopWatch,
  count => {
    if (count === '00:00:00') {
      hideVideo()
    }
  }
)

onMounted(async () => {
  if (isLiveGoods.value) {
    await checkWifi()
    await autoPlay()
  }
})

onActivated(async () => {
  if (isLiveGoods.value) {
    await checkWifi()
    await autoPlay()
  }
})

// hooks
onUnmounted(() => {
  resetThumbsSwiper()
  hideVideo()
})

function autoPlay() {
  if (isAutoVideo.value) openVideo()
}

function openVideo() {
  let src = ''

  const detailInfo = goodsInfo.value?.detailInfo
  if (isLiveGoods.value) {
    if (detailInfo?.busChnId === 'TV') {
      src = liveVodFilePath.value
    } else if (detailInfo?.busChnId === 'CTCOM') {
      src = shopPlusVodFilePath.value
    }
  } else if (pastVodFilePath.value) {
    src = pastVodFilePath.value
  }

  showVideo()
  state.videoSrc = src
}

function onLoadImg() {
  isImgLoaded.value = true
  emit('onLoad', isImgLoaded.value)
}
</script>

<template>
  <div class="goods-detail-img">
    <div class="img-info">
      <div class="img-swiper">
        <!-- mobile -->
        <Swiper v-if="!isDesktop" :modules="[Pagination, A11y]" :pagination="isPagination">
          <SwiperSlide v-for="(item, index) in goodsPhotoList" :key="index" :item="item">
            <div v-if="(isLiveGoods && index === 0) || pastVodFilePath" class="item is-video">
              <GoodsImageDetail
                :goods-cd="Number(goodsInfo?.detailInfo?.goodsCd)"
                :config="{
                  width: MOBILE_IMAGE_SIZE.DETAIL_UNIT.REPRESENTATIVE_IMAGE.WIDTH,
                  height: MOBILE_IMAGE_SIZE.DETAIL_UNIT.REPRESENTATIVE_IMAGE.HEIGHT,
                  alt: goodsInfo?.detailInfo?.productNm,
                  isAdult: goodsInfo?.detailInfo?.adltGoodsYn === 'Y',
                  index: index
                }"
                @on-load="onLoadImg"
              />
              <button
                v-if="(!isShowVideo && isLiveGoods && !isTimeOut) || pastVodFilePath"
                type="button"
                class="btn-play"
                @click="openVideo"
              >
                play
              </button>
            </div>
            <button type="button" v-else class="item" @click="() => (state.isShowLayer = true)">
              <GoodsImageDetail
                :goods-cd="Number(goodsInfo?.detailInfo?.goodsCd)"
                :config="{
                  width: MOBILE_IMAGE_SIZE.DETAIL_UNIT.REPRESENTATIVE_IMAGE.WIDTH,
                  height: MOBILE_IMAGE_SIZE.DETAIL_UNIT.REPRESENTATIVE_IMAGE.HEIGHT,
                  alt: goodsInfo?.detailInfo?.productNm,
                  isAdult: goodsInfo?.detailInfo?.adltGoodsYn === 'Y',
                  index: index
                }"
                @on-load="onLoadImg"
              />
            </button>
          </SwiperSlide>
        </Swiper>

        <!-- desktop -->
        <Swiper v-else :modules="[Thumbs, A11y]" :thumbs="{ swiper: thumbsSwiper }">
          <SwiperSlide v-for="(item, index) in goodsPhotoList" :key="index" :item="item">
            <div v-if="(isLiveGoods && index === 0) || pastVodFilePath" class="item is-video">
              <GoodsImageDetail
                :goods-cd="Number(goodsInfo?.detailInfo?.goodsCd)"
                :config="{
                  width: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.WIDTH,
                  height: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.HEIGHT,
                  alt: goodsInfo?.detailInfo?.productNm,
                  isAdult: goodsInfo?.detailInfo?.adltGoodsYn === 'Y',
                  index: index
                }"
                @on-load="onLoadImg"
              />
              <button
                v-if="(!isShowVideo && isLiveGoods && !isTimeOut) || pastVodFilePath"
                type="button"
                class="btn-play"
                @click="openVideo"
              >
                play
              </button>
            </div>
            <button type="button" v-else class="item" @click="() => (state.isShowLayer = true)">
              <GoodsImageDetail
                :goods-cd="Number(goodsInfo?.detailInfo?.goodsCd)"
                :config="{
                  width: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.WIDTH,
                  height: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.HEIGHT,
                  alt: goodsInfo?.detailInfo?.productNm,
                  isAdult: goodsInfo?.detailInfo?.adltGoodsYn === 'Y',
                  index: index
                }"
                @on-load="onLoadImg"
              />
            </button>
          </SwiperSlide>
        </Swiper>

        <!-- mobile -->
        <div v-if="!isDesktop && !isGroupItem" class="pagination"></div>
        <ModalDetailView v-model="state.isShowLayer" :photoList="goodsPhotoList" :items="goodsInfo" />
      </div>

      <Videos v-if="state.videoSrc" :src="state.videoSrc" />
    </div>

    <!-- desktop -->
    <div class="detail-add-swiper" v-if="isDesktop && !isGroupItem">
      <Swiper
        @swiper="setThumbsSwiper"
        :modules="[A11y, Thumbs]"
        :space-between="8"
        :slides-per-view="5"
        :watchSlidesProgress="true"
      >
        <SwiperSlide v-for="(item, index) in goodsPhotoList" :key="index" :item="item">
          <button type="button" class="item">
            <GoodsImageDetail
              :goods-cd="Number(goodsInfo?.detailInfo?.goodsCd)"
              :config="{
                width: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.WIDTH,
                height: MOBILE_IMAGE_SIZE.DETAIL_UNIT.DETAIL.HEIGHT,
                alt: goodsInfo?.detailInfo?.productNm,
                isAdult: goodsInfo?.detailInfo?.adltGoodsYn === 'Y',
                index: index
              }"
              @on-load="onLoadImg"
            />
          </button>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '~/pages/goods/scss/detail-img.scss';
</style>
